import type { PeriodRow, ViewerEntry } from './timetableViewerDisplay'

/** UNTIS-üblich: 1 = Mo … 7 = So (entspricht JS getDay mit Sonntag → 7). */
export function untisWeekdayFromDate(d: Date): number {
  const js = d.getDay()
  return js === 0 ? 7 : js
}

export function parseTimeToMinutes(t: string | null | undefined): number | null {
  if (t == null) return null
  const s = String(t).trim()
  const match = /^(\d{1,2}):(\d{2})(?::(\d{2}))?/.exec(s)
  if (!match) return null
  const h = Number(match[1])
  const m = Number(match[2])
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null
  return h * 60 + m
}

export type TimetableHighlightResult = {
  todayWd: number
  todayInGrid: boolean
  currentWeekday: number | null
  currentPeriodNo: number | null
  nextWeekday: number | null
  nextPeriodNo: number | null
}

type LessonSlot = {
  weekday: number
  period_no: number
  startM: number | null
  endM: number | null
}

function hasLesson(cellMap: Map<string, ViewerEntry[]>, weekday: number, periodNo: number): boolean {
  return (cellMap.get(`${weekday}:${periodNo}`) || []).length > 0
}

/** Sortierbare Position innerhalb einer Schulwoche (Mo=0 …). */
function weekMinuteOffset(weekday: number, startM: number | null, periodNo: number): number {
  const dayBase = (weekday - 1) * 24 * 60
  if (startM != null) return dayBase + startM
  return dayBase + periodNo * 15
}

function buildLessonSlots(
  weekdays: number[],
  periodRows: PeriodRow[],
  cellMap: Map<string, ViewerEntry[]>
): LessonSlot[] {
  const sortedWd = [...weekdays].sort((a, b) => a - b)
  const sortedPeriods = [...periodRows].sort((a, b) => a.period_no - b.period_no)
  const slots: LessonSlot[] = []

  for (const wd of sortedWd) {
    for (const p of sortedPeriods) {
      if (!hasLesson(cellMap, wd, p.period_no)) continue
      slots.push({
        weekday: wd,
        period_no: p.period_no,
        startM: parseTimeToMinutes(p.start_time),
        endM: parseTimeToMinutes(p.end_time)
      })
    }
  }

  return slots
}

export function computeTimetableHighlights(
  now: Date,
  todayWd: number,
  weekdays: number[],
  periodRows: PeriodRow[],
  cellMap: Map<string, ViewerEntry[]>
): TimetableHighlightResult {
  const todayInGrid = weekdays.includes(todayWd)
  const empty: TimetableHighlightResult = {
    todayWd,
    todayInGrid,
    currentWeekday: null,
    currentPeriodNo: null,
    nextWeekday: null,
    nextPeriodNo: null
  }

  if (!weekdays.length || !periodRows.length) {
    return empty
  }

  const sorted = [...periodRows].sort((a, b) => a.period_no - b.period_no)
  const nowM = now.getHours() * 60 + now.getMinutes()
  const lessonSlots = buildLessonSlots(weekdays, sorted, cellMap)

  if (!lessonSlots.length) {
    return empty
  }

  let currentWeekday: number | null = null
  let currentPeriodNo: number | null = null

  if (todayInGrid) {
    for (const p of sorted) {
      const sm = parseTimeToMinutes(p.start_time)
      const em = parseTimeToMinutes(p.end_time)
      if (sm != null && em != null && em > sm && nowM >= sm && nowM < em) {
        currentWeekday = todayWd
        currentPeriodNo = p.period_no
        break
      }
    }
  }

  const nowOffset = weekMinuteOffset(todayWd, nowM, 0)

  let nextSlot: LessonSlot | null = null
  for (const slot of lessonSlots) {
    const slotOffset = weekMinuteOffset(slot.weekday, slot.startM, slot.period_no)
    if (slotOffset > nowOffset) {
      nextSlot = slot
      break
    }
  }

  if (!nextSlot) {
    nextSlot = lessonSlots[0]
  }

  return {
    todayWd,
    todayInGrid,
    currentWeekday,
    currentPeriodNo,
    nextWeekday: nextSlot.weekday,
    nextPeriodNo: nextSlot.period_no
  }
}
